import type { ApiClient } from './client';
import { route } from './route';
import { AITHORA_PREFIX } from './prefix';
import type {
  FrameworkEnableResult,
  GovernancePoliciesResponse,
  GuardianKillSwitchResult,
  PlaybookCreateBody,
  PlaybooksResponse,
  PlaybookTestFire,
  PolicyAlwaysRules,
  PolicyAutonomyResponse,
  PolicyAutonomyTest,
  PolicyOverrideResult,
  PolicyRuleDraft,
  RuleAction,
  PolicyBreakersResponse,
  PolicyDraftBody,
  PolicyGuardCatchesResponse,
  PolicyGuardFire,
  PolicyGuardsResponse,
  PolicyGuardStatsResponse,
  PolicyPresetsResponse,
  PolicyReplay,
  PolicyRequestBody,
  PolicyRequestResult,
  PolicyRequestsResponse,
  PolicyRuleCreated,
  PolicyRuleModeResult,
  PolicyRulesResponse,
  PolicyScopeResponse,
  PolicyScopeSettingsBody,
  PolicyScopeSummary,
  PolicyVersionDiff,
  PolicyVersionResult,
  PolicyVersionsResponse,
  RuleMode,
  RuleSpec,
} from './policy-types';

const A = AITHORA_PREFIX;
/** `scope` is 'network' or a facility id; a Facility Admin's is always their own building (the server ignores it). */
type ScopeQ = { scope?: string };
/** facilities: the facility switcher's pick (Super Admin, Operator) on the network scope: the fires follow it. */
type FiresQ = ScopeQ & { facilities?: string };

// ── Guards and telemetry (v3/policy.ts 1b, 1e) ─────────────────────────────

/** GET /policy/guards?scope=: every guard, its live network setting and this building's override. */
export const policyGuards = route<PolicyGuardsResponse, FiresQ>()('GET', `${A}/policy/guards`, { honest: true });
/** GET /policy/guards/stats?scope=&days=7: fires per day, last fired, how close to the trip line. */
export const policyGuardStats = route<PolicyGuardStatsResponse, FiresQ & { days?: number }>()('GET', `${A}/policy/guards/stats`, { honest: true });
/** GET /policy/guards/:key/catches?scope=&limit=3: "Last caught". */
export const policyGuardCatches = route<PolicyGuardCatchesResponse, FiresQ & { limit?: number }>()('GET', `${A}/policy/guards/:key/catches`, { honest: true });

// ── Custom rules (1d) ──────────────────────────────────────────────────────

/** GET /policy/rules?scope=: a building sees its own rules and the network's. */
export const policyRules = route<PolicyRulesResponse, ScopeQ>()('GET', `${A}/policy/rules`, { honest: true });
/** POST /policy/rules: live at once for the owner; otherwise a change request for the owner. */
export const policyRuleCreate = route<PolicyRuleCreated, void, RuleSpec & { scope?: string; confirm?: true }>()('POST', `${A}/policy/rules`, { honest: true });
/** PATCH /policy/rules/:id/mode: watch to enforce is a request; enforce to watch needs a reason (Aithora only). */
export const policyRuleMode = route<PolicyRuleModeResult, void, { mode: RuleMode; reason?: string; confirm?: true }>()('PATCH', `${A}/policy/rules/:id/mode`, { honest: true });
/** GET /policy/rules/:id/replay?days=7: what the rule would have caught, on real records. */
export const policyRuleReplay = route<PolicyReplay, { days?: number }>()('GET', `${A}/policy/rules/:id/replay`, { honest: true });

// ── Versions (1a) ──────────────────────────────────────────────────────────

/** GET /policy/versions?scope= */
export const policyVersions = route<PolicyVersionsResponse, ScopeQ & { limit?: number }>()('GET', `${A}/policy/versions`, { honest: true });
/** GET /policy/versions/:id/diff */
export const policyVersionDiff = route<PolicyVersionDiff>()('GET', `${A}/policy/versions/:id/diff`, { honest: true });
/** POST /policy/versions: a network draft (Aithora staff). */
export const policyVersionDraft = route<PolicyVersionResult, void, PolicyDraftBody & { confirm?: true }>()('POST', `${A}/policy/versions`, { honest: true });
/** POST /policy/versions/:id/rollback: a NEW draft carrying the old snapshot. */
export const policyVersionRollback = route<PolicyVersionResult, void, { confirm: true }>()('POST', `${A}/policy/versions/:id/rollback`, { honest: true });
/** POST /policy/versions/:id/publish: make a draft live (sealed to the Truth Ledger). */
export const policyVersionPublish = route<PolicyVersionResult, void, { confirm: true }>()('POST', `${A}/policy/versions/:id/publish`, { honest: true });

// ── Change requests (1c) ───────────────────────────────────────────────────

/** GET /policy/requests?status=: Aithora sees every building's, a Facility Admin only their own. */
export const policyRequests = route<PolicyRequestsResponse, { status?: 'pending' | 'approved' | 'declined' }>()('GET', `${A}/policy/requests`, { honest: true });
/** POST /policy/requests: a Facility Admin's change for their own building. */
export const policyRequestCreate = route<PolicyRequestResult, void, PolicyRequestBody & { confirm?: true }>()('POST', `${A}/policy/requests`, { honest: true });
/** POST /policy/requests/:id/approve: the owner (or the deputy the owner named) only. */
export const policyRequestApprove = route<PolicyRequestResult, void, { confirm: true }>()('POST', `${A}/policy/requests/:id/approve`, { honest: true });
/** POST /policy/requests/:id/decline {reason}: the owner (or deputy) only; a reason is required. */
export const policyRequestDecline = route<PolicyRequestResult, void, { reason: string; confirm: true }>()('POST', `${A}/policy/requests/:id/decline`, { honest: true });
/** POST /policy/requests/:id/recommend: Aithora staff advise; the owner decides. */
export const policyRequestRecommend = route<PolicyRequestResult, void, { recommendation: 'approve' | 'decline'; note?: string }>()(
  'POST', `${A}/policy/requests/:id/recommend`, { honest: true });

// ── Scope, posture, always-rules (1f, 1g) ─────────────────────────────────

/** GET /policy/scope: blocked topics and the two time limits (network only). */
export const policyScope = route<PolicyScopeResponse>()('GET', `${A}/policy/scope`, { honest: true });
/** PUT /policy/scope: Aithora staff; writes a published network version. */
export const policyScopeUpdate = route<PolicyScopeResponse, void, PolicyScopeSettingsBody & { confirm?: true }>()('PUT', `${A}/policy/scope`, { honest: true });
/** GET /policy/always-rules: "Rules that always apply" (read-only). */
export const policyAlwaysRules = route<PolicyAlwaysRules>()('GET', `${A}/policy/always-rules`, { honest: true });

// ── Playbooks, governance policies, frameworks, the global pause (server.ts) ──

/** GET /api/playbooks: the response rules and their fire history. */
export const playbooks = route<PlaybooksResponse>()('GET', '/api/playbooks');
/** POST /api/playbooks */
export const playbookCreate = route<{ success: boolean; playbook: PlaybooksResponse['playbooks'][number] }, void, PlaybookCreateBody & { confirm?: true }>()('POST', '/api/playbooks');
/** POST /api/playbooks/:id/toggle */
export const playbookToggle = route<{ success: boolean }, void, { confirm?: true }>()('POST', '/api/playbooks/:id/toggle');
/** DELETE /api/playbooks/:id */
export const playbookDelete = route<{ success: boolean }>()('DELETE', '/api/playbooks/:id');
/** POST /api/playbooks/:id/test-fire: a dry run; nothing executes. */
export const playbookTestFire = route<PlaybookTestFire, void, Record<string, never>>()('POST', '/api/playbooks/:id/test-fire');
/** GET /api/policies: the governance gate's policies and stats. */
export const governancePolicies = route<GovernancePoliciesResponse>()('GET', '/api/policies');
/** POST /api/frameworks/:id/enable: seal a framework's bundled policies onto the gate. */
export const frameworkEnable = route<FrameworkEnableResult, void, { confirm: true }>()('POST', '/api/frameworks/:id/enable');
/** POST /api/guardian/kill-switch: stop (anyone allowed) or restart (the owner only) the AI. */
export const guardianKillSwitch = route<GuardianKillSwitchResult, void, { action: 'engage' | 'release'; scope: 'org' | 'global'; reason?: string; organizationId?: string; confirm: true }>()(
  'POST', '/api/guardian/kill-switch');

// ── Autonomy, breakers, presets, scope line, rule drafting and upkeep (served, be-govern) ──

/** GET /policy/autonomy: the autonomy matrix (stored, versioned; not enforced yet, says so). */
export const policyAutonomy = route<PolicyAutonomyResponse>()('GET', `${A}/policy/autonomy`, { honest: true });
/**
 * PUT /policy/autonomy {levels: {rowKey: 1-5}}: Aithora staff. Lowering applies at once;
 * raising is the owner's alone (anyone else's raise is a change request, HTTP 202).
 */
export const policyAutonomyUpdate = route<PolicyAutonomyResponse, void, { levels: Record<string, number>; confirm: true }>()('PUT', `${A}/policy/autonomy`, { honest: true });
/** POST /policy/autonomy/test {levels}: a dry run of a matrix change (saves nothing). */
export const policyAutonomyTest = route<PolicyAutonomyTest, void, { levels: Record<string, number> }>()('POST', `${A}/policy/autonomy/test`, { honest: true, readOnlySafe: true });
/** GET /policy/breakers?scope=: circuit-breaker tiers (not enforced: real false) and the live trip count. */
export const policyBreakers = route<PolicyBreakersResponse, ScopeQ>()('GET', `${A}/policy/breakers`, { honest: true });
/** GET /policy/playbook-presets: the pre-armed response sequences (described, not wired: real false). */
export const policyPresets = route<PolicyPresetsResponse>()('GET', `${A}/policy/playbook-presets`, { honest: true });
/** GET /policy/scope-summary?scope=: the facility line (name, facilities, residents). */
export const policyScopeSummary = route<PolicyScopeSummary, ScopeQ>()('GET', `${A}/policy/scope-summary`, { honest: true });
/** POST /policy/rules/draft {text}: a rule's parts from plain words (it saves nothing, calls no model). */
export const policyRuleDraft = route<PolicyRuleDraft, void, { text: string }>()('POST', `${A}/policy/rules/draft`, { honest: true, readOnlySafe: true });
/** POST /policy/rules/preview?days=7: the replay of a rule not saved yet (it saves nothing). */
export const policyRulePreview = route<PolicyReplay, { days?: number }, RuleSpec & { scope?: string }>()('POST', `${A}/policy/rules/preview`, { honest: true, readOnlySafe: true });
/**
 * DELETE /policy/rules/:id {reason?, confirm}: a pending rule is withdrawn; a live rule is
 * removed by the owner directly, or anyone else's removal waits for the owner (HTTP 202).
 */
export const policyRuleDelete = route<PolicyRuleModeResult, void, { reason?: string; confirm: true }>()('DELETE', `${A}/policy/rules/:id`, { honest: true });
/** PATCH /policy/rules/:id/action {action, reason?, confirm}: the owner directly; anyone else asks (HTTP 202). */
export const policyRuleAction = route<PolicyRuleModeResult, void, { action: RuleAction; reason?: string; confirm: true }>()('PATCH', `${A}/policy/rules/:id/action`, { honest: true });
/**
 * PUT /policy/overrides {scope: facilityId, overrides, note?, confirm}: Aithora staff set a
 * building's override. The owner applies it (versioned, sealed); anyone else's waits for
 * the owner (HTTP 202). Safety floors are refused (422).
 */
export const policyOverrideWrite = route<PolicyOverrideResult, void, { scope: string; overrides: Record<string, boolean>; note?: string; confirm: true }>()(
  'PUT', `${A}/policy/overrides`, { honest: true });

/**
 * GET /policy/events/stream (SSE, event "guard.fire"): the live ticker. Reads frames as
 * they arrive and calls onFire for each; resolves when the stream ends or is aborted.
 * `since` (ms) resumes from a moment, so the ticker opens with the last hour. An answer
 * that is not an event stream is treated as no live feed (onFire is never called).
 */
export async function policyEventsStream(
  client: ApiClient,
  opts: { scope?: string; since?: number; onFire: (f: PolicyGuardFire) => void; signal?: AbortSignal },
): Promise<{ live: boolean }> {
  const qs = new URLSearchParams();
  if (opts.scope) qs.set('scope', opts.scope);
  if (opts.since) qs.set('since', String(opts.since));
  const q = qs.toString();
  const res = await client.raw(`${A}/policy/events/stream${q ? `?${q}` : ''}`, { headers: { Accept: 'text/event-stream' }, signal: opts.signal });
  const type = res.headers.get('content-type') ?? '';
  if (!res.ok || !type.includes('text/event-stream') || !res.body) return { live: false };
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    let cut = buf.indexOf('\n\n');
    while (cut >= 0) {
      const block = buf.slice(0, cut);
      buf = buf.slice(cut + 2);
      const ev = /^event: (.+)$/m.exec(block)?.[1]?.trim();
      const data = /^data: (.*)$/m.exec(block)?.[1];
      if (ev === 'guard.fire' && data !== undefined) {
        try {
          opts.onFire(JSON.parse(data) as PolicyGuardFire);
        } catch {
          /* a malformed frame is skipped */
        }
      }
      cut = buf.indexOf('\n\n');
    }
  }
  return { live: true };
}

export const policyApi = {
  policyGuards,
  policyGuardStats,
  policyGuardCatches,
  policyRules,
  policyRuleCreate,
  policyRuleMode,
  policyRuleReplay,
  policyVersions,
  policyVersionDiff,
  policyVersionDraft,
  policyVersionRollback,
  policyVersionPublish,
  policyRequests,
  policyRequestCreate,
  policyRequestApprove,
  policyRequestDecline,
  policyRequestRecommend,
  policyScope,
  policyScopeUpdate,
  policyAlwaysRules,
  playbooks,
  playbookCreate,
  playbookToggle,
  playbookDelete,
  playbookTestFire,
  governancePolicies,
  frameworkEnable,
  guardianKillSwitch,
  policyAutonomy,
  policyAutonomyUpdate,
  policyAutonomyTest,
  policyBreakers,
  policyPresets,
  policyScopeSummary,
  policyRuleDraft,
  policyRulePreview,
  policyRuleDelete,
  policyRuleAction,
  policyOverrideWrite,
} as const;
